(function () {
  var SECTION_TYPE = 'journey-section';
  var ytApiPromise = null;

  function loadYouTubeApi() {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise(function (resolve) {
      if (window.YT && typeof window.YT.Player === 'function') {
        resolve(window.YT);
        return;
      }
      var prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === 'function') {
          try { prev(); } catch (e) {}
        }
        resolve(window.YT);
      };
      if (!document.querySelector('script[data-ps-yt], script[data-hero-lo-yt], script[data-jrn-yt]')) {
        var tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        tag.setAttribute('data-jrn-yt', '');
        document.head.appendChild(tag);
      }
    });
    return ytApiPromise;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function trackInsetLeft(track) {
    var cs = window.getComputedStyle(track);
    var v = parseFloat(cs.scrollPaddingInlineStart);
    if (isNaN(v)) v = parseFloat(cs.paddingInlineStart);
    return isNaN(v) ? 0 : v;
  }

  function indexFromScroll(track, items) {
    if (!items.length) return 0;
    var anchor = track.getBoundingClientRect().left + trackInsetLeft(track);
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < items.length; i++) {
      var d = Math.abs(items[i].getBoundingClientRect().left - anchor);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  function scrollItemToStart(track, item) {
    if (!item) return;
    var trackRect = track.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    var delta = itemRect.left - trackRect.left - trackInsetLeft(track);
    if (Math.abs(delta) < 1) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });
  }

  /* -------- Journey controller -------- */
  function setupJourney(container) {
    var cardsTrack = container.querySelector('[data-jrn-cards]');
    if (!cardsTrack) return null;
    var cards = Array.prototype.slice.call(cardsTrack.querySelectorAll('[data-jrn-card]'));
    if (!cards.length) return null;
    var pills = Array.prototype.slice.call(container.querySelectorAll('[data-jrn-pill]'));
    var fill = container.querySelector('[data-jrn-fill]');
    var nextBtn = container.querySelector('[data-jrn-next]');
    var count = cards.length;
    var activeIndex = 0;
    var suppressScrollSync = false;

    function setActive(i, opts) {
      i = Math.max(0, Math.min(count - 1, i));
      activeIndex = i;
      cards.forEach(function (card, idx) {
        var on = idx === i;
        card.classList.toggle('is-active', on);
        if (on) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current');
      });
      pills.forEach(function (pill, idx) {
        var on = idx === i;
        pill.classList.toggle('is-active', on);
        pill.classList.toggle('is-completed', idx <= i);
        if (on) pill.setAttribute('aria-current', 'true'); else pill.removeAttribute('aria-current');
      });
      if (fill) {
        var scale = count > 1 ? i / (count - 1) : 0;
        container.style.setProperty('--jrn-fill-scale', scale);
      }
      if (!opts || opts.scroll !== false) {
        suppressScrollSync = true;
        scrollItemToStart(cardsTrack, cards[i]);
        setTimeout(function () { suppressScrollSync = false; }, 500);
      }
    }

    cards.forEach(function (card) {
      var btn = card.querySelector('[data-jrn-card-btn]') || card;
      btn.addEventListener('click', function () {
        var idx = parseInt(card.getAttribute('data-index'), 10) || 0;
        setActive(idx);
      });
    });

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var idx = parseInt(pill.getAttribute('data-index'), 10) || 0;
        setActive(idx);
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        setActive(activeIndex + 1 < count ? activeIndex + 1 : 0);
      });
    }

    var syncFromScroll = debounce(function () {
      if (suppressScrollSync) return;
      var idx = indexFromScroll(cardsTrack, cards);
      if (idx !== activeIndex) setActive(idx, { scroll: false });
    }, 120);

    cardsTrack.addEventListener('scroll', syncFromScroll, { passive: true });

    cardsTrack.setAttribute('tabindex', '0');
    cardsTrack.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Home')       { e.preventDefault(); setActive(0); }
      else if (e.key === 'End')        { e.preventDefault(); setActive(count - 1); }
    });

    // Initial fill
    setActive(0, { scroll: false });

    return {
      destroy: function () {
        cardsTrack.removeEventListener('scroll', syncFromScroll);
      }
    };
  }

  /* -------- Crew controller -------- */
  function setupCrew(container) {
    var cardsTrack = container.querySelector('[data-crew-cards]');
    if (!cardsTrack) return null;
    var cards = Array.prototype.slice.call(cardsTrack.querySelectorAll('[data-crew-card]'));
    if (!cards.length) return null;
    var media = Array.prototype.slice.call(container.querySelectorAll('[data-crew-media]'));
    var prevBtn = container.querySelector('[data-crew-prev]');
    var nextBtn = container.querySelector('[data-crew-next]');
    var count = cards.length;
    var activeIndex = 0;
    var suppressScrollSync = false;
    var sectionVisible = true;
    var ytPlayers = {};
    var vimeoMounted = {};

    function pauseHtml5(video) {
      if (!video) return;
      try { video.pause(); } catch (e) {}
      try { video.currentTime = 0; } catch (e) {}
    }

    function playHtml5(video) {
      if (!video) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    function activateExternalVideo(item) {
      var v = item.querySelector('video[data-src]');
      if (!v) return;
      if (!v.src) v.src = v.getAttribute('data-src');
      playHtml5(v);
    }

    function mountYouTubeFor(item, idx) {
      var host = item.querySelector('[data-yt-host]');
      var videoId = item.getAttribute('data-youtube-id');
      if (!host || !videoId) return;
      if (ytPlayers[idx]) {
        try { ytPlayers[idx].playVideo(); } catch (e) {}
        return;
      }
      loadYouTubeApi().then(function (YT) {
        var mount = document.createElement('div');
        host.innerHTML = '';
        host.appendChild(mount);
        ytPlayers[idx] = new YT.Player(mount, {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 1,
            mute: 1,
            loop: 1,
            playlist: videoId,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1
          },
          events: {
            onReady: function (e) {
              try { e.target.mute(); e.target.playVideo(); } catch (err) {}
              item.setAttribute('data-poster-hidden', 'true');
            }
          }
        });
      });
    }

    function pauseYouTubeFor(idx) {
      var p = ytPlayers[idx];
      if (p && typeof p.pauseVideo === 'function') {
        try { p.pauseVideo(); } catch (e) {}
        try { p.seekTo(0); } catch (e) {}
      }
    }

    function mountVimeoFor(item, idx) {
      if (vimeoMounted[idx]) {
        var existing = item.querySelector('iframe');
        if (existing) {
          var sep = existing.src.indexOf('?') === -1 ? '?' : '&';
          if (existing.src.indexOf('autoplay=1') === -1) existing.src = existing.src + sep + 'autoplay=1';
        }
        return;
      }
      var host = item.querySelector('[data-vimeo-host]');
      var videoId = item.getAttribute('data-vimeo-id');
      if (!host || !videoId) return;
      var iframe = document.createElement('iframe');
      iframe.src = 'https://player.vimeo.com/video/' + videoId + '?autoplay=1&muted=1&loop=1&controls=1&playsinline=1&dnt=1';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('loading', 'lazy');
      iframe.title = 'Video';
      host.innerHTML = '';
      host.appendChild(iframe);
      vimeoMounted[idx] = true;
      iframe.addEventListener('load', function () { item.setAttribute('data-poster-hidden', 'true'); });
    }

    function pauseVimeoFor(item, idx) {
      if (!vimeoMounted[idx]) return;
      var iframe = item.querySelector('iframe');
      if (!iframe) return;
      try { iframe.contentWindow.postMessage('{"method":"pause"}', '*'); } catch (e) {}
    }

    function activateMedia(idx) {
      media.forEach(function (item, i) {
        var on = i === idx;
        item.classList.toggle('is-active', on);
        var mode = item.getAttribute('data-video-mode');
        if (!on) {
          if (mode === 'file' || mode === 'external') pauseHtml5(item.querySelector('video'));
          if (mode === 'youtube') pauseYouTubeFor(i);
          if (mode === 'vimeo') pauseVimeoFor(item, i);
        } else if (sectionVisible) {
          if (mode === 'file') playHtml5(item.querySelector('video'));
          else if (mode === 'external') activateExternalVideo(item);
          else if (mode === 'youtube') mountYouTubeFor(item, i);
          else if (mode === 'vimeo') mountVimeoFor(item, i);
        }
      });
    }

    function setActive(i, opts) {
      var n = count;
      i = ((i % n) + n) % n;
      activeIndex = i;
      cards.forEach(function (card, idx) {
        var on = idx === i;
        card.classList.toggle('is-active', on);
        if (on) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current');
      });
      activateMedia(i);
      if (!opts || opts.scroll !== false) {
        suppressScrollSync = true;
        scrollItemToStart(cardsTrack, cards[i]);
        setTimeout(function () { suppressScrollSync = false; }, 500);
      }
    }

    cards.forEach(function (card) {
      var btn = card.querySelector('[data-crew-card-btn]') || card;
      btn.addEventListener('click', function () {
        var idx = parseInt(card.getAttribute('data-index'), 10) || 0;
        setActive(idx);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { setActive(activeIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setActive(activeIndex + 1); });

    var syncFromScroll = debounce(function () {
      if (suppressScrollSync) return;
      var idx = indexFromScroll(cardsTrack, cards);
      if (idx !== activeIndex) setActive(idx, { scroll: false });
    }, 120);
    cardsTrack.addEventListener('scroll', syncFromScroll, { passive: true });

    cardsTrack.setAttribute('tabindex', '0');
    cardsTrack.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); setActive(activeIndex - 1); }
    });

    // Pause everything when section is out of view; resume on return.
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sectionVisible = entry.isIntersecting;
          if (!sectionVisible) {
            media.forEach(function (item, i) {
              var mode = item.getAttribute('data-video-mode');
              if (mode === 'file' || mode === 'external') pauseHtml5(item.querySelector('video'));
              if (mode === 'youtube') pauseYouTubeFor(i);
              if (mode === 'vimeo') pauseVimeoFor(item, i);
            });
          } else {
            activateMedia(activeIndex);
          }
        });
      }, { rootMargin: '0px', threshold: 0.15 });
      io.observe(container);
    }

    // Initial activation
    activateMedia(0);

    return {
      destroy: function () {
        cardsTrack.removeEventListener('scroll', syncFromScroll);
        if (io) io.disconnect();
      }
    };
  }

  function init(container) {
    if (!container || container.dataset.jrnBound === 'true') return;
    container.dataset.jrnBound = 'true';
    var jrn = setupJourney(container);
    var crew = setupCrew(container);
    container._jrnCleanup = function () {
      if (jrn && typeof jrn.destroy === 'function') jrn.destroy();
      if (crew && typeof crew.destroy === 'function') crew.destroy();
    };
  }

  function destroy(container) {
    if (!container) return;
    if (typeof container._jrnCleanup === 'function') container._jrnCleanup();
    container.dataset.jrnBound = '';
  }

  function bootstrap(root) {
    (root || document).querySelectorAll('[data-section-type="' + SECTION_TYPE + '"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootstrap(); });
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (e) { bootstrap(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-section-type="' + SECTION_TYPE + '"]').forEach(destroy);
  });
  document.addEventListener('shopify:section:select', function (e) { bootstrap(e.target); });
})();
